// client/src/components/servicos/GaleriaBuscaModal.js
import React, { useState, useEffect } from 'react';
import { useGaleria } from '../../hooks/useGaleria';
import { useSegmentos } from '../../hooks/useSegmentos';
import Pagination from '../common/Pagination';
import FormModal from '../layout/FormModal';
import { Search, Loader2, Eraser, Filter as FilterIcon } from 'lucide-react';

// ✅ CORREÇÃO: O componente agora aceita a prop 'item' (de ItemVenda) e usa 'servico' como fallback.
const GaleriaBuscaModal = ({ onClose, onImageSelect, servico, item }) => {
    const [showFilters, setShowFilters] = useState(true);
    const [filters, setFilters] = useState({ nome: '', segmento: '', categorias: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [hasSearched, setHasSearched] = useState(false);
    const itemsPerPage = 8;
    
    // ✅ Define qual objeto usar (item ou serviço)
    const entidade = item || servico;

    const { data: segmentos, isLoading: isLoadingSegmentos } = useSegmentos();
    const { data: galeriaData, isLoading: isLoadingGaleria } = useGaleria({
        page: currentPage,
        limit: itemsPerPage,
        ...filters
    });

    useEffect(() => {
        // Usa a categoria da entidade para pré-filtrar
        if (entidade?.categoria) {
            setFilters(prev => ({ ...prev, categorias: entidade.categoria }));
        }
    }, [entidade]);

    const handleFilterChange = (e) => setFilters({ ...filters, [e.target.name]: e.target.value });
    const handleSearchClick = (page = 1) => {
        setCurrentPage(page);
        setHasSearched(true);
    };
    const clearFiltersAndSearch = () => {
        setFilters({ nome: '', segmento: '', categorias: '' });
        setCurrentPage(1);
        setHasSearched(false);
    };
    const paginate = (pageNumber) => {
        setCurrentPage(pageNumber);
        setHasSearched(true);
    };

    // ✅ Usa a 'entidade' para pegar a lista de imagens já vinculadas
    const jaVinculadasIds = (entidade?.imagensDaGaleria || []).map(img => img._id);
    const imagens = galeriaData?.imagens || [];
    const count = galeriaData?.count || 0;
    const isLoading = isLoadingGaleria || isLoadingSegmentos;

    const filterToggleButton = (
        <button type="button" onClick={() => setShowFilters(p => !p)} className="action-button action-button-secondary py-1 px-2 text-sm">
            <FilterIcon size={16} className="icon"/>
            {showFilters ? 'Ocultar Filtros' : 'Exibir Filtros'}
        </button>
    );

    return (
        <FormModal 
            title="Buscar Imagem na Galeria" 
            onClose={onClose} 
            size="large"
            headerControls={filterToggleButton}
        >
            {showFilters && (
                <div className="content-panel mb-6 -mx-6 -mt-6 border-b">
                    <div className="form-grid--auto-fit p-6">
                        <div className="form-group">
                            <label htmlFor="nome-filtro-modal">Pesquisar por Nome</label>
                            <input 
                                type="search" 
                                id="nome-filtro-modal" 
                                name="nome" 
                                value={filters.nome} 
                                onChange={handleFilterChange} 
                                placeholder="Digite o nome..." 
                                className="form-input"
                            />
                        </div>
                        <div className="form-group">
                            <label htmlFor="segmento-filtro-modal">Filtrar por Segmento</label>
                            <select 
                                name="segmento" 
                                id="segmento-filtro-modal" 
                                value={filters.segmento} 
                                onChange={handleFilterChange} 
                                className="form-input"
                                disabled={isLoadingSegmentos}
                            >
                                <option value="">Todos</option>
                                {segmentos?.map(segmento => (
                                    <option key={segmento._id} value={segmento._id}>
                                        {segmento.nome}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="form-group">
                            <label htmlFor="categorias-filtro-modal">Filtrar por Categorias</label>
                            <input 
                                type="text" 
                                id="categorias-filtro-modal" 
                                name="categorias" 
                                value={filters.categorias} 
                                onChange={handleFilterChange} 
                                placeholder="Ex: depilacao, laser" 
                                className="form-input"
                            />
                        </div>
                        <div className="form-actions">
                            <button 
                                type="button" 
                                onClick={clearFiltersAndSearch} 
                                className="action-button action-button-secondary" 
                                disabled={isLoading}
                            >
                                <Eraser className="icon" /> Limpar
                            </button>
                            <button 
                                type="button" 
                                onClick={() => handleSearchClick(1)} 
                                className="action-button action-button-primary" 
                                disabled={isLoading}
                            >
                                <Search className="icon" /> Buscar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="content-panel bg-transparent p-0 border-none">
                <div className="table-header">
                    <h2>Resultados da Busca ({isLoading ? '...' : count})</h2>
                </div>
                {isLoading ? (
                    <div className="panel-state-wrapper" style={{minHeight: '200px'}}>
                        <Loader2 className="animate-spin" size={32}/>
                    </div>
                ) : (
                    <>
                        <div className="image-gallery-grid mt-4 is-selectable">
                            {!hasSearched ? (
                                <div className="panel-state-wrapper panel-info col-span-full">
                                    <Search size={24} className="icon"/>
                                    <span>Use os filtros e clique em "Buscar" para encontrar imagens.</span>
                                </div>
                            ) : imagens.length > 0 ? (
                                imagens.map(img => {
                                    const isLinked = jaVinculadasIds.includes(img._id);
                                    return (
                                        <div 
                                            key={img._id} 
                                            className={`gallery-item-card as-selectable ${isLinked ? 'is-linked' : ''}`} 
                                            onClick={() => !isLinked && onImageSelect(img)} 
                                            title={isLinked ? "Imagem já vinculada" : `Selecionar: ${img.nome}`}
                                        >
                                            <div className="gallery-item-card__image-container">
                                                <img src={img.url} alt={img.nome} />
                                                {isLinked && <div className="linked-overlay"><span>Vinculada</span></div>}
                                            </div>
                                            <div className="gallery-item-card__body">
                                                <h5 title={img.nome}>{img.nome}</h5>
                                                <p>{img.descricao || 'Sem descrição'}</p>
                                                <div className="tags-container">
                                                    {(img.categorias || []).map(cat => (
                                                        <span key={cat} className="badge badge-blue">{cat}</span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    )
                                })
                            ) : (
                                <div className="panel-state-wrapper panel-info col-span-full">
                                    <Search size={24} className="icon"/>
                                    <span>Nenhuma imagem encontrada com os filtros aplicados.</span>
                                </div>
                            )}
                        </div>
                        <div className="mt-6 flex justify-center">
                            {hasSearched && count > itemsPerPage && !isLoading && 
                                <Pagination 
                                    currentPage={currentPage} 
                                    totalItems={count} 
                                    itemsPerPage={itemsPerPage} 
                                    onPageChange={paginate} 
                                />
                            }
                        </div>
                    </>
                )}
            </div>
        </FormModal>
    );
};

export default GaleriaBuscaModal;